import React, { useState, useEffect, useRef } from "react";
import { Toaster } from "react-hot-toast";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import logo from "../assets/logo.png";
import bgHero from "../assets/First-Responders-hero-img.png";
import appImg1 from "../assets/app-img1.JPG";
import appImg2 from "../assets/app-img2.JPG";
import appImg3 from "../assets/app-img3.JPG";
import responder1 from "../assets/responders1.JPG";
import responders2 from "../assets/responders2.JPG";
import responders3 from "../assets/responders3.JPG";

/** Stripe setup — allow TEST or LIVE publishable keys */
const pk = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
if (!pk || !/^pk_(test|live)_/.test(pk)) {
  throw new Error("Missing or invalid REACT_APP_STRIPE_PUBLISHABLE_KEY");
}
const stripePromise = loadStripe(pk);

/** ---- Create-Password Modal ---- */
function PasswordModal({ open, onClose }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 200);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) return setErr("Password must be at least 8 characters.");
    setBusy(true);
    try {
      const base = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8080";
      const res = await fetch(`${base}/api/auth/set-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password: pw }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        setErr(data?.error || "Failed to set password.");
      } else {
        if (data.token) {
          try {
            localStorage.setItem("token", data.token);
          } catch {}
        }
        setDone(true);
        setTimeout(() => {
          window.location.assign("/dashboard");
        }, 900);
      }
    } catch (e) {
      setErr("Network error. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-[71] w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl">
        <h4 className="text-lg font-semibold">Create your password</h4>
        <p className="mt-1 text-sm text-gray-600">
          You’re in! Set a password to access your challenge dashboard.
        </p>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <input
            ref={inputRef}
            type="password"
            placeholder="New password (min 8 chars)"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-black"
          />
          {err && <p className="text-sm text-red-600">{err}</p>}
          <button
            type="submit"
            disabled={busy || done}
            className="w-full rounded-lg bg-[#e53935] px-6 py-3 text-white hover:bg-black disabled:opacity-60"
          >
            {done ? "Password set! Redirecting…" : busy ? "Saving…" : "Save password"}
          </button>
          <p className="text-xs text-gray-600">You can change this later in settings.</p>
        </form>
      </div>
    </div>
  );
}

/** ---- Stripe Payment Box ---- */
function CheckoutForm() {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Return to ROOT with purchase=success
        return_url: `${window.location.origin}/?purchase=success`,
      },
    });

    if (error) setMessage(error.message || "Payment failed.");
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      <button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full rounded-lg bg-[#e53935] px-6 py-3 text-white hover:bg-black disabled:opacity-60"
      >
        {submitting ? "Processing…" : "Join Now – Start Your Reset"}
      </button>
      {message && <p className="text-sm text-red-600">{message}</p>}
      <p className="text-xs text-gray-600">You'll be logged in immediately after checkout.</p>
    </form>
  );
}

/** Creates a PaymentIntent for a given product */
function PaymentBox({ productId = "7day" }) {
  const [clientSecret, setClientSecret] = useState(null);

  useEffect(() => {
    const base = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8080";

    // pull email from the free guide step to send with PI creation
    let email;
    try {
      const raw = sessionStorage.getItem("frt_free_guide_signup");
      email = raw ? JSON.parse(raw)?.email : undefined;
    } catch {
      email = undefined;
    }

    fetch(`${base}/api/payments/create-intent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ productId, amount: 700, email }),
    })
      .then((r) => r.json())
      .then((d) => setClientSecret(d.clientSecret))
      .catch((e) => {
        console.error("Create intent error:", e);
        setClientSecret("error");
      });
  }, [productId]);

  if (clientSecret === "error") {
    return <div className="text-sm text-red-600">Checkout failed to load. Please refresh.</div>;
  }

  if (!clientSecret) {
    return <div className="text-sm text-gray-500">Loading checkout…</div>;
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: "stripe" } }}>
      <CheckoutForm />
    </Elements>
  );
}

/** Simple modal for checkout */
function CheckoutModal({ open, onClose }) {
  const firstBtnRef = useRef(null);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => firstBtnRef.current?.focus(), 200);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      aria-modal="true"
      role="dialog"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-[61] w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm text-gray-600">🔒 Secure Checkout</div>
          <button
            ref={firstBtnRef}
            onClick={onClose}
            className="rounded px-2 py-1 text-sm text-gray-600 hover:bg-gray-100"
            aria-label="Close checkout"
          >
            ✕
          </button>
        </div>
        <PaymentBox productId="7day" />
      </div>
    </div>
  );
}

/** Thank-you strip after free guide */
function ThankYouStrip() {
  const [visible, setVisible] = useState(true);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("frt_free_guide_signup");
      if (!raw) {
        // No signup stored → do NOT show the strip
        setMsg("");
        return;
      }

      const data = JSON.parse(raw);
      const first = data?.firstName?.trim();
      const email = data?.email?.trim();
      const namePart = first ? `, ${first}` : "";
      const emailPart = email ? ` (${email})` : "";

      setMsg(
        `Thanks${namePart}! Your Free Reset Guide is on its way${emailPart}. Now let’s put it into action.`
      );
    } catch {
      // If parsing fails, also hide the strip
      setMsg("");
    }
  }, []);

  if (!visible || !msg) return null;

  return (
    <div className="w-full bg-[#ecfdf5] border-b border-emerald-200">
      <div className="mx-auto flex max-w-6xl items-start gap-3 px-4 py-3 text-sm text-emerald-900">
        <span className="mt-0.5">✅</span>
        <p className="flex-1">{msg}</p>
        <button
          onClick={() => setVisible(false)}
          className="text-emerald-700 hover:underline"
        >
          Hide
        </button>
      </div>
    </div>
  );
}

/** ---------- LANDING PAGE ---------- */
export default function LandingPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [ctaBusy, setCtaBusy] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [sessionMsg, setSessionMsg] = useState("");

  // consume magic token from email to restore identity and set frt_uid
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const t = p.get("t");
    if (!t) return;
    const base = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8080";
    fetch(`${base}/api/funnel/consume-token?t=${encodeURIComponent(t)}`, {
      method: "POST",
      credentials: "include",
    })
      .then(() => {
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("t");
          window.history.replaceState({}, "", url.toString());
        } catch {}
      })
      .catch(() => {});
  }, []);

  // clear any old JWT at the start of this funnel page
  useEffect(() => {
    try {
      localStorage.removeItem("token");
    } catch {}
  }, []);

  // After purchase success, check /me and show password modal if needed
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const purchaseSuccess = p.get("purchase") === "success";
    if (!purchaseSuccess) return;

    const base = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8080";
    fetch(`${base}/api/auth/me`, { method: "GET", credentials: "include" })
      .then(async (r) => {
        if (r.status === 401) {
          setSessionMsg(
            "Session expired. Please grab the free guide again or use the email link we sent to continue."
          );
          return;
        }
        const d = await r.json().catch(() => ({}));
        if (d?.ok) {
          if (d.hasPassword) {
            window.location.assign("/dashboard");
          } else {
            setPwOpen(true);
          }
        }
      })
      .catch(() => {});
  }, []);

  const openCheckout = () => {
    setCtaBusy(true);
    setCheckoutOpen(true);
    setTimeout(() => setCtaBusy(false), 250);
    try {
      window?.gtag?.("event", "begin_checkout", {
        value: 7,
        currency: "USD",
        items: [{ id: "7day" }],
      });
    } catch {}
  };

  const FAQ = [
    {
      q: "What’s the difference between the free guide and the $7 challenge?",
      a: "The guide is the plan; the challenge is guided execution. You get daily mission emails, reminders, simple tracking, and the app trial so you actually finish it.",
    },
    {
      q: "Do I need to be in shape?",
      a: "No. Every reset is designed for any experience level.",
    },
    {
      q: "Is this therapy?",
      a: "No — it’s a guided mental & physical reset system built with responders and military.",
    },
    {
      q: "When do I start Day 1?",
      a: "You should already have the PDF atached in the email we've sent you. You can start on your own right away or guided in app.",
    },
    {
      q: "What if I miss a day?",
      a: "Each day’s drill stays unlocked in your inbox — go at your own pace.",
    },
    {
      q: "Can family members join?",
      a: "Yes — families are welcome to complete it together.",
    },
    {
      q: "Refund policy?",
      a: "If you don’t feel better by Day 3, email us for a full refund.",
    },
  ];

  return (
    <div className="relative flex min-h-screen flex-col bg-white">
      {/* background image + readability gradient */}
      <div className="pointer-events-none absolute inset-0 z-0">
        <img
          src={bgHero}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover object-center opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/85 via-white/60 to-white/20" />
      </div>

      {/* content layer */}
      <div className="relative z-10">
        <Toaster position="top-center" reverseOrder={false} />

        {/* HEADER / NAV */}
        <header className="sticky top-0 z-20 w-full bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/50">
          <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-4 py-3 sm:py-4">
            <div className="flex items-center gap-2">
              <img
                src={logo}
                alt="First Response Therapy"
                className="h-8 w-auto"
              />
              <span className="sr-only">First Response Therapy</span>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={() => window.location.assign("/login")}
                className="whitespace-nowrap rounded-lg bg-[#e53935] text-white font-semibold hover:bg-black
                            px-3 py-1.5 text-xs
                            sm:px-4 sm:py-2 sm:text-sm"
              >
                Log in
              </button>
            </div>
          </div>{" "}
          {/* ← THIS WAS MISSING */}
        </header>

        {/* THANK YOU STRIP (from free guide) */}
        <ThankYouStrip />

        {/* session expired strip */}
        {sessionMsg && (
          <div className="w-full bg-red-50 border-b border-red-200">
            <div className="mx-auto max-w-6xl px-4 py-3 text-sm text-red-800">
              {sessionMsg}
            </div>
          </div>
        )}

        {/* HERO */}
        <main className="flex items-center min-h-[72vh] md:min-h-[74vh] lg:min-h-[72vh]">
          <div className="mx-auto w-full max-w-6xl px-4">
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
              {/* Left copy */}
              <div className="text-center lg:text-left">
                <p className="text-sm font-medium text-gray-700">
                  You’ve got the guide — now turn it into results, one day at a
                  time.
                </p>
                <h1 className="mt-2 text-3xl font-extrabold leading-tight sm:text-4xl">
                  7-Day Guided Reset Challenge for People Who Serve
                </h1>
                <h2 className="mt-3 text-lg text-gray-700 sm:text-xl">
                  Overcome burnout • Improve sleep & focus • Boost physical
                  readiness
                </h2>

                <div className="mt-6 flex flex-col items-center gap-2 sm:flex-row lg:items-start">
                  <button
                    onClick={openCheckout}
                    disabled={ctaBusy}
                    className="w-full rounded-lg bg-[#e53935] px-6 py-3 text-white hover:bg-black disabled:opacity-60 sm:w-auto"
                  >
                    {ctaBusy
                      ? "Opening…"
                      : "Join the 7-Day Guided Reset – $7"}
                  </button>
                  <div className="text-sm text-gray-600">
                    <span role="img" aria-label="lock">
                      🔒
                    </span>{" "}
                    One-time payment · No subscription · Built with and for
                    responders, military and families
                  </div>
                </div>

                {/* Guide vs Challenge mini-card */}
                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-lg border bg-white p-4 text-left shadow-sm">
                    <div className="text-xs font-semibold uppercase text-gray-500">
                      Free Guide
                    </div>
                    <ul className="mt-2 list-disc pl-5 text-sm text-gray-700 space-y-1">
                      <li>The full 7-day plan</li>
                      <li>Self-paced reading</li>
                      <li>Do it on your own</li>
                    </ul>
                  </div>
                  <div className="rounded-lg border bg-white p-4 text-left shadow-sm">
                    <div className="text-xs font-semibold uppercase text-gray-500">
                      $7 Guided Challenge
                    </div>
                    <ul className="mt-2 list-disc pl-5 text-sm text-gray-700 space-y-1">
                      <li>In app daily missions</li>
                      <li>Simple progress tracking</li>
                      <li>Included app trial</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Right visual (keep Payment box visible next to hero) */}
              <div className="flex items-center justify-center">
                <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white/80 p-6 shadow">
                  <PaymentBox productId="7day" />
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* SECTION 2 – WHY IT WORKS */}
        <section className="py-16">
          <div className="mx-auto max-w-6xl px-4">
            <h3 className="text-center text-2xl font-bold">
              Real Tools. Real Results. Designed for People Who Serve.
            </h3>

            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <div className="rounded-lg border bg-white p-6 shadow-sm">
                <div className="mb-2 text-2xl">⏱️</div>
                <h4 className="font-semibold">Fast & Practical</h4>
                <p className="mt-1 text-gray-600">
                  10-minute daily resets that fit your shift life.
                </p>
              </div>
              <div className="rounded-lg border bg-white p-6 shadow-sm">
                <div className="mb-2 text-2xl">🧑‍🚒</div>
                <h4 className="font-semibold">Built with Responders</h4>
                <p className="mt-1 text-gray-600">
                  Created in consultation with firefighters, medics, LEOs and
                  military.
                </p>
              </div>
              <div className="rounded-lg border bg-white p-6 shadow-sm">
                <div className="mb-2 text-2xl">🎯</div>
                <h4 className="font-semibold">No Fluff</h4>
                <p className="mt-1 text-gray-600">
                  Real actions, no lectures or therapy talk.
                </p>
              </div>
            </div>

            {/* Testimonials */}
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[
                ["Finally something that fits my life.", "— Firefighter, Ohio"],
                ["Better sleep by Day 3.", "— EMT, Texas"],
                ["This made me want to talk to someone again.", "— LEO, Florida"],
              ].map(([t, who], i) => (
                <div
                  key={i}
                  className="rounded-lg border bg-white p-5 shadow-sm"
                >
                  <div className="text-xl">💬</div>
                  <p className="mt-2 italic text-gray-800">“{t}”</p>
                  <p className="mt-1 text-sm text-gray-600">{who}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 3 – WHAT YOU GET */}
        <section className="bg-white py-16">
          <div className="mx-auto max-w-6xl px-4">
            <h3 className="text-center text-2xl font-bold">
              Here’s What Happens Over the Next 7 Days
            </h3>

            <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[
                ["Day 1", "Breathing for Calm", "🌬️"],
                ["Day 2", "Sleep Recovery", "🛌"],
                ["Day 3", "Boost Your Mood", "🏋️"],
                ["Day 4", "Eat for Energy", "🍽️"],
                ["Day 5", "Improve Emotional Control", "🧘"],
                ["Day 6", "Reduce Stress", "📱"],
                ["Day 7", "Integration Plan", "📝"],
              ].map(([day, title, icon]) => (
                <div
                  key={day}
                  className="rounded-lg border bg-white p-5 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">{icon}</div>
                    <div>
                      <div className="text-xs font-semibold uppercase text-gray-500">
                        {day}
                      </div>
                      <div className="font-semibold">{title}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-6 text-center text-gray-600">
              Total mind & body recalibration, all within one dashboard.
            </p>

            <div className="mt-6 text-center">
              <button
                onClick={openCheckout}
                disabled={ctaBusy}
                className="rounded-lg bg-[#e53935] px-6 py-3 text-white hover:bg-black disabled:opacity-60"
              >
                {ctaBusy ? "Opening…" : "Start the 7-Day Guided Reset for $7 →"}
              </button>
            </div>
          </div>
        </section>

        {/* SECTION 4 – SOCIAL PROOF */}
        <section className="py-16">
          <div className="mx-auto max-w-6xl px-4">
            <h3 className="text-center text-2xl font-bold">
              You’re Not Alone — Hundreds of Responders Are Resetting Daily.
            </h3>

            <div className="mt-8 grid gap-6 lg:grid-cols-3">
              <div className="rounded-lg border bg-white p-6 shadow-sm text-center">
                <img
                  src={responder1}
                  alt="Responder testimonial 1"
                  className="h-40 w-full rounded-md object-cover"
                  loading="lazy"
                />
                <p className="mt-3 italic text-gray-800">
                  “The small steps really worked.”
                </p>
              </div>
              <div className="rounded-lg border bg-white p-6 shadow-sm text-center">
                <img
                  src={responders2}
                  alt="Responder testimonial 2"
                  className="h-40 w-full rounded-md object-cover"
                  loading="lazy"
                />
                <p className="mt-3 italic text-gray-800">
                  “Our whole crew did it together.”
                </p>
              </div>
              <div className="rounded-lg border bg-white p-6 shadow-sm text-center">
                <img
                  src={responders3}
                  alt="Responder testimonial 3"
                  className="h-40 w-full rounded-md object-cover"
                  loading="lazy"
                />
                <p className="mt-3 italic text-gray-800">
                  “Finally something made for us, not at us.”
                </p>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-3 items-center gap-4 opacity-70 sm:grid-cols-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-8 rounded bg-gray-2 00" />
              ))}
            </div>

            <div className="mt-6 text-center text-sm text-gray-700">
              <span className="font-semibold">92%</span> said they felt calmer
              by Day 4.
            </div>
          </div>
        </section>

        {/* SECTION 5 – OFFER & CHECKOUT */}
        <section className="bg-white py-16">
          <div className="mx-auto max-w-6xl px-4">
            <h3 className="text-center text-2xl font-bold">
              Join the 7-Day Responder Reset Challenge — Just $7
            </h3>

            <div className="mt-8 grid gap-8 lg:grid-cols-2">
              {/* Left: bullets */}
              <div className="rounded-lg border bg-white p-6 shadow-sm">
                <ul className="space-y-3">
                  <li>✅ Daily missions with a clear drill</li>
                  <li>✅ Accountability reminders</li>
                  <li>✅ Simple progress tracking</li>
                  <li>✅ Included app trial</li>
                </ul>
                <p className="mt-4 text-gray-600">
                  One-time charge. No subscription required. Cancel anytime.
                </p>
                <p className="mt-1 text-sm text-gray-600">
                  Log in instantly after checkout.
                </p>
                <div className="mt-6">
                  <button
                    onClick={openCheckout}
                    disabled={ctaBusy}
                    className="rounded-lg bg-[#e53935] px-6 py-3 text-white hover:bg-black disabled:opacity-60"
                  >
                    {ctaBusy ? "Opening…" : "Join Now – Start Your Reset"}
                  </button>
                </div>
              </div>

              {/* Right: same exact payment box as hero */}
              <div className="rounded-lg border bg-white p-6 shadow-lg">
                <div className="mb-4 text-sm text-gray-600">
                  🔒 Secure Checkout · Instant Access · Built with People Who
                  Serve{" "}
                </div>
                <div className="w-full">
                  <PaymentBox productId="7day" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6 – BONUS / NEXT STEP */}
        <section className="bg-gray-50 py-16">
          <div className="mx-auto max-w-6xl px-4">
            <h3 className="text-center text-2xl font-bold">
              Keep Your Momentum Going After Day 7
            </h3>
            <p className="mt-3 text-center text-gray-700">
              After completing the challenge, unlock full access to the First
              Response Therapy App — with daily workouts, meditation, and sleep
              tracking.
            </p>

            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <div className="rounded-lg border bg-white p-6 shadow-sm">
                <img
                  src={appImg1}
                  alt="First Response Therapy app preview 1"
                  className="h-48 w-full rounded-md object-cover"
                  loading="lazy"
                />
                <p className="mt-3 text-sm text-gray-600">
                  Quick mental check-ins
                </p>
              </div>
              <div className="rounded-lg border bg-white p-6 shadow-sm">
                <img
                  src={appImg2}
                  alt="First Response Therapy app preview 2"
                  className="h-48 w-full rounded-md object-cover"
                  loading="lazy"
                />
                <p className="mt-3 text-sm text-gray-600">
                  Track your workouts
                </p>
              </div>
              <div className="rounded-lg border bg-white p-6 shadow-sm">
                <img
                  src={appImg3}
                  alt="First Response Therapy app preview 3"
                  className="h-48 w-full rounded-md object-cover"
                  loading="lazy"
                />
                <p className="mt-3 text-sm text-gray-600">
                  Talk to someone that gets it
                </p>
              </div>
            </div>

            <div className="mt-8 text-center">
              <button
                onClick={openCheckout}
                disabled={ctaBusy}
                className="rounded-lg bg-[#e53935] px-6 py-3 text-white hover:bg-black disabled:opacity-60"
              >
                {ctaBusy ? "Opening…" : "First, Join the $7 Challenge →"}
              </button>
            </div>
          </div>
        </section>

        {/* SECTION 7 – FAQ */}
        <section className="py-16">
          <div className="mx-auto max-w-6xl px-4">
            <h3 className="text-center text-2xl font-bold">
              Got Questions? We’ve Got Answers.
            </h3>

            <div className="mx-auto mt-8 max-w-3xl divide-y rounded-md border bg-white">
              {FAQ.map((item, idx) => {
                const open = openFaq === idx;
                return (
                  <div key={idx}>
                    <button
                      className="flex w-full items-center justify-between px-4 py-4 text-left"
                      onClick={() => setOpenFaq(open ? null : idx)}
                    >
                      <span className="font-medium">{item.q}</span>
                      <span className="text-xl">{open ? "−" : "+"}</span>
                    </button>
                    {open && (
                      <div className="px-4 pb-4 text-gray-700">{item.a}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* SECTION 8 – FINAL CTA */}
        <section
          className="py-16 text-white"
          style={{ backgroundColor: "#3935e5" }}
        >
          <div className="mx-auto max-w-4xl px-4 text-center">
            <h3 className="text-2xl font-bold">
              You serve everyone else. Take 7 days to serve yourself.
            </h3>
            <button
              onClick={openCheckout}
              disabled={ctaBusy}
              className="mt-6 rounded-lg px-8 py-3 font-semibold text-white hover:opacity-90 disabled:opacity-60"
              style={{ backgroundColor: "#e53935" }}
            >
              {ctaBusy ? "Opening…" : "Join the 7-Day Guided Reset – $7"}
            </button>
            <p className="mt-3 text-sm text-white/90">
              <span
                className="font-semibold"
                style={{ color: "#35e539" }}
              >
                ✓
              </span>{" "}
              Secure Checkout · Instant Access · Built with Responders
            </p>
          </div>
        </section>
      </div>

      {/* Modals rendered at root level */}
      <CheckoutModal
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
      />
      <PasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}


